import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  fetchExcelTemplateForProducts,
  postExcelProductTemplate,
} from "../api/importProducts.api";

const useImportProductsFromExcel = () => {
  const { t } = useTranslation();
  const [bearerToken, toast, companyId, organizationId] = useUIStore(
    useShallow((state: UIState) => [
      state.bearerToken,
      state.toastRef,
      state.companyId,
      state.organizationId,
    ]),
  );

  const downloadExcelTemplate = async () => {
    try {
      const data = await fetchExcelTemplateForProducts(bearerToken);
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Error downloading Excel template:", error);
    }
  };

  const postProductExcel = async (file: File, existingReportId?: number) => {
    try {
      const response = await postExcelProductTemplate(
        file,
        bearerToken as string,
        companyId as number,
        organizationId ? organizationId : undefined,
        existingReportId,
      );

      if (response && response.responseType === "excel") {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ecomodtemplate.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);

        toast?.current?.show({
          severity: "success",
          detail: t("excel-phase-1-success"),
          life: 5000,
        });

        // The prefilled excel returned here can't carry a JSON body, so any
        // warnings/errors are reported via headers instead; surface them
        // through the same summary dialog as a completed import.
        if (response.warnings?.length || response.errors?.length) {
          return { errors: response.errors, warnings: response.warnings };
        }
      }

      // If the response is 201, it means a complete excel file was uploaded
      // and we can create products from the data. Caller shows the summary
      // dialog and refreshes the list once the user acknowledges it.
      if (response && response.responseType === "json") {
        return response.data;
      }
    } catch (error) {
      console.error("Error uploading Excel file:", error);
      toast?.current?.show({
        severity: "error",
        summary: t("excel-upload-error"),
        detail: error instanceof Error ? error.message : undefined,
        life: 8000,
      });
      throw error;
    }
  };
  return { downloadExcelTemplate, postProductExcel };
};

export default useImportProductsFromExcel;
